import { ago, Drawer, Empty, fmtDateTime, Loading, Pill, setQuery, useLoad, useRoute } from "./lib";

const CHECK: Record<string, { label: string; tone: string }> = {
  confirmed: { label: "Confirmed", tone: "good" },
  not_found: { label: "Not found", tone: "warn" },
  not_checkable: { label: "Can't check", tone: "" },
};
const RUN: Record<string, { label: string; tone: string }> = { ok: { label: "OK", tone: "good" }, partial: { label: "Partial", tone: "info" }, failed: { label: "Failed", tone: "warn" } };

// Every Claude agent: whether it ran, what each run claims and what the portal confirmed, and how useful its items were.
export function Agents() {
  const route = useRoute();
  const { data, error } = useLoad<any[]>("/api/agents", [route.q.get("run")]);
  if (!data) return <div className="page"><Loading error={error} /></div>;
  return (
    <div className="page">
      <p className="muted small" style={{ margin: 0 }}>
        Each agent leaves a receipt after every run. The portal checks the receipt against its own records. The scorecard comes from your choices on each agent's items in Today.
      </p>
      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))" }}>
        {data.map((a) => <AgentCard key={a.key} a={a} />)}
      </div>
      {route.q.get("run") && <RunDrawer id={route.q.get("run")!} onClose={() => setQuery({ run: null })} />}
    </div>
  );
}

function Stat({ n, label, hint }: { n: React.ReactNode; label: string; hint?: string }) {
  return (
    <div className="stack" style={{ gap: 0 }} title={hint}>
      <strong style={{ fontSize: 20 }}>{n ?? "—"}</strong>
      <span className="muted tiny">{label}</span>
    </div>
  );
}

function AgentCard({ a }: { a: any }) {
  const last = a.runs[0];
  const w = a.week;
  const bad = (r: any) => r.checks.filter((c: any) => c.result === "not_found").length;
  return (
    <section className="card stack">
      <div className="row between">
        <div className="stack" style={{ gap: 2 }}>
          <strong>{a.name}</strong>
          <span className="muted tiny">{a.schedule}</span>
        </div>
        {!last ? <Pill>No receipts yet</Pill> : a.missed ? <Pill tone="warn">Missed its last run</Pill> : <Pill tone={RUN[last.status]?.tone}>{`Ran ${ago(last.started_at)}`}</Pill>}
      </div>

      <div className="tiny" style={{ fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted)" }}>Last 7 days</div>
      <div className="row" style={{ gap: 22, flexWrap: "wrap" }}>
        <Stat n={w.runs} label="runs" />
        {a.key === "follow_through" ? (
          <>
            <Stat n={(w.actions?.drafted ?? 0) + (w.actions?.done ?? 0)} label="drafts made" />
            <Stat n={w.actions?.failed ?? 0} label="failed" />
          </>
        ) : (
          <>
            <Stat n={w.items} label="items posted" />
            <Stat n={w.useful_pct == null ? null : `${w.useful_pct}%`} label="useful" hint="Used as suggested, or used with changes, out of the items you decided" />
            <Stat n={w.open} label="still open" />
          </>
        )}
        <Stat n={w.mismatches} label="claims not found" hint="Things a receipt said happened that the portal couldn't find" />
      </div>
      {a.key !== "follow_through" && w.items > 0 && (
        <span className="muted tiny">
          Of the ones you decided: {w.used} used as suggested, {w.changed} changed, {w.not_needed} not needed. 30 days: {a.month.items} items, {a.month.useful_pct == null ? "no decisions yet" : `${a.month.useful_pct}% useful`}.
        </span>
      )}

      <div>
        {a.runs.length ? (
          a.runs.slice(0, 6).map((r: any) => (
            <div key={r.id} className="row between clickable" role="button" tabIndex={0} style={{ padding: "6px 4px", borderRadius: 8 }} onClick={() => setQuery({ run: r.id })} onKeyDown={(e) => e.key === "Enter" && setQuery({ run: r.id })}>
              <span className="small">{fmtDateTime(r.started_at)} <span className="muted">· {r.summary ? r.summary.slice(0, 70) + (r.summary.length > 70 ? "…" : "") : "No summary"}</span></span>
              <span className="row" style={{ gap: 6 }}>
                {bad(r) > 0 && <Pill tone="warn">{bad(r)} not found</Pill>}
                <Pill tone={RUN[r.status]?.tone}>{RUN[r.status]?.label ?? r.status}</Pill>
              </span>
            </div>
          ))
        ) : (
          <Empty>Receipts start with its next run.</Empty>
        )}
      </div>
    </section>
  );
}

function RunDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: r, error } = useLoad<any>(`/api/agent/runs/${id}`);
  const head = (t: string) => <div className="tiny" style={{ fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted)", marginTop: 8 }}>{t}</div>;
  return (
    <Drawer title={r ? `${r.agent_name} · receipt` : "Receipt"} sub={r && `${fmtDateTime(r.started_at)} to ${fmtDateTime(r.finished_at)}`} onClose={onClose}>
      {!r ? (
        <Loading error={error} />
      ) : (
        <div className="stack">
          <div className="row" style={{ gap: 8 }}><Pill tone={RUN[r.status]?.tone}>{RUN[r.status]?.label ?? r.status}</Pill></div>
          {r.summary && <p style={{ margin: 0 }}>{r.summary}</p>}

          {head("What the portal checked")}
          {r.checks.length ? r.checks.map((c: any, i: number) => (
            <div key={i} className="row between" style={{ gap: 10, alignItems: "flex-start" }}>
              <div className="stack" style={{ gap: 0 }}><span className="small">{c.claim}</span>{c.detail && <span className="muted tiny">{c.detail}</span>}</div>
              <Pill tone={CHECK[c.result]?.tone}>{CHECK[c.result]?.label ?? c.result}</Pill>
            </div>
          )) : <span className="muted small">The run claimed nothing the portal can check.</span>}

          {r.items.length > 0 && (
            <>
              {head("What happened to its items")}
              {r.items.map((it: any, i: number) => (
                <div key={i} className="small">
                  {it.title}
                  <span className="muted"> · {it.status === "open" ? "still open" : it.status === "missing" ? "not in Today" : it.choice ?? it.status}{it.note ? `, "${it.note}"` : ""}</span>
                </div>
              ))}
            </>
          )}

          {r.read.length > 0 && (
            <>
              {head("What it says it read")}
              {r.read.map((x: any, i: number) => <div key={i} className="small">{x.href ? <a href={x.href} target="_blank" rel="noreferrer">{x.label}</a> : x.label}</div>)}
            </>
          )}

          {r.could_not_check.length > 0 && (
            <>
              {head("What it couldn't check")}
              {r.could_not_check.map((x: any, i: number) => <div key={i} className="small">{x.what}{x.why && <span className="muted"> · {x.why}</span>}</div>)}
            </>
          )}

          {r.instructions_found && (
            <div className="banner warn small"><div><strong>Text that tried to direct the agent: </strong>{r.instructions_found} It didn't act on it.</div></div>
          )}
        </div>
      )}
    </Drawer>
  );
}
